/**
 * ScreenHost: owns the GuiStage the container screens live in and routes
 * mouse / keyboard input to the open one.
 *
 * Lead wiring:
 *  - open*() releases pointer lock itself (document.exitPointerLock).
 *  - Forward every `keydown` to `handleKey(e)` while `open`; it returns true when
 *    the event was consumed (always while a screen is open) and handles Esc / E
 *    (close), 1-9 (hotbar swap over the hovered slot), Q / Ctrl+Q (drop) and the
 *    creative search field's typing itself. So do NOT also toggle E yourself while
 *    open -- ask `open` first, otherwise open the inventory.
 *  - Mouse input is attached to the stage itself (mousedown/up/move, wheel,
 *    contextmenu suppressed); nothing to forward.
 *  - After Esc-closing, re-request pointer lock from a user gesture (the key press
 *    counts) in `onClosed`.
 *  - `deps.renderPlayerPreview(canvas, lookX, lookY)`: canvas is already sized to the
 *    preview box in *device* px (49x70 GUI px survival, 32x43 creative); lookX/lookY
 *    are vanilla's values: GUI px offset of the pointer from the preview's focus
 *    point (positive lookX = pointer left of the player).
 *  - Slots hold the player's real Stack objects and are mutated in place; `onChange`
 *    fires after every click that changed something.
 */

import type { GameMode } from '../../game/types';
import type { Inventory } from '../../game/inventory';
import type { FurnaceState } from '../../game/furnace';
import type { BrewingStandState } from '../../game/brewing/stand';
import type { Stack } from '../../game/types';
import type { ItemIcons } from '../itemIcons';
import { GuiStage, Tooltip } from '../kit';
import { ContainerScreen } from './container';
import { CreativeScreen } from './creativeScreen';
import { ChestScreen, CraftingTableScreen, FurnaceScreen, InventoryScreen } from './survivalScreens';
import { EnchantScreen, type EnchantCtx } from './enchantScreen';
import { AnvilScreen, type AnvilCtx } from './anvilScreen';
import { GrindstoneScreen, type GrindstoneCtx } from './grindstoneScreen';
import { SmithingScreen } from './smithingScreen';
import { BrewingScreen } from './brewingScreen';

export interface ScreenDeps {
  inventory: Inventory;
  icons: ItemIcons;
  gameMode: () => GameMode;
  /** GUI scale setting 0..4 (0 = auto). */
  guiScale: () => number;
  /** Throw a stack into the world in front of the player. */
  dropStack: (s: Stack) => void;
  /** Called whenever any slot changed. */
  onChange: () => void;
  renderPlayerPreview: (canvas: HTMLCanvasElement, lookX: number, lookY: number) => void;
  /** Award experience (furnace output). */
  addXp?: (n: number) => void;
  /** Server mode (client/windows.ts): every click a shared-menu screen applied, to send as a `click` intent. */
  remote?: ((c: import('../../game/menu').ClickArgs) => void) | null;
}

export class ScreenHost {
  readonly stage: GuiStage;
  onClosed: (() => void) | null = null;
  private current: ContainerScreen | null = null;
  private closeCb: (() => void) | null = null;
  private inv: InventoryScreen | null = null;
  private table: CraftingTableScreen | null = null;
  private creative: CreativeScreen | null = null;
  private readonly dim: HTMLDivElement;
  private lastScale = -1;
  private px = 0;
  private py = 0;

  constructor(parent: HTMLElement, readonly deps: ScreenDeps) {
    this.stage = new GuiStage(parent, 50);
    this.stage.hide();
    this.dim = document.createElement('div');
    // vanilla renderBackground: vertical gradient 0xC0101010 -> 0xD0101010
    this.dim.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;background:linear-gradient(rgba(16,16,16,0.753),rgba(16,16,16,0.816));';
    this.stage.content.appendChild(this.dim);
    const el = this.stage.el;
    el.addEventListener('mousedown', (e) => {
      if (!this.current) return;
      e.preventDefault();
      this.current.pointerMove(e.clientX, e.clientY);
      if (e.button !== 1) this.current.pointerDown(e.button === 2 ? 1 : 0, e.shiftKey);
      this.current.refresh(false);
    });
    window.addEventListener('mouseup', (e) => {
      if (!this.current) return;
      this.current.pointerMove(e.clientX, e.clientY);
      if (e.button !== 1) this.current.pointerUp(e.button === 2 ? 1 : 0);
      this.current.refresh(false);
    });
    window.addEventListener('mousemove', (e) => {
      this.px = e.clientX; this.py = e.clientY;
      if (this.current) this.current.pointerMove(e.clientX, e.clientY);
    });
    el.addEventListener('contextmenu', (e) => e.preventDefault());
    el.addEventListener('wheel', (e) => {
      const c = this.current as (ContainerScreen & { wheel?: (d: number) => void }) | null;
      c?.wheel?.(e.deltaY);
      e.preventDefault();
    }, { passive: false });
    window.addEventListener('resize', () => this.relayout());
  }

  get open(): boolean { return this.current !== null; }
  /** The open screen (client/windows.ts reads its shared menu and cursor). */
  get screen(): ContainerScreen | null { return this.current; }
  get creativeOpen(): boolean { return this.current !== null && this.current === this.creative; }
  /** The survival / creative inventory is the open screen (the status-effect list sits beside it). */
  get inventoryOpen(): boolean { return this.current !== null && (this.current === this.inv || this.current === this.creative); }

  openInventory(): void {
    if (this.deps.gameMode() === 'creative') this.show((this.creative ??= new CreativeScreen(this.deps, this.stage)));
    else this.show((this.inv ??= new InventoryScreen(this.deps, this.stage)));
  }
  openCraftingTable(): void {
    this.show((this.table ??= new CraftingTableScreen(this.deps, this.stage)));
  }
  openFurnace(state: FurnaceState, onClose?: () => void): void {
    this.show(new FurnaceScreen(this.deps, this.stage, state), onClose);
  }
  openBrewing(state: BrewingStandState, onClose?: () => void): void {
    this.show(new BrewingScreen(this.deps, this.stage, state), onClose);
  }
  openEnchanting(ctx: EnchantCtx, onClose?: () => void): void {
    this.show(new EnchantScreen(this.deps, this.stage, ctx), onClose);
  }
  openSmithing(): void {
    this.show(new SmithingScreen(this.deps, this.stage));
  }
  openAnvil(ctx: AnvilCtx, onClose?: () => void): void {
    this.show(new AnvilScreen(this.deps, this.stage, ctx), onClose);
  }
  openGrindstone(ctx: GrindstoneCtx, onClose?: () => void): void {
    this.show(new GrindstoneScreen(this.deps, this.stage, ctx), onClose);
  }
  openChest(contents: Stack[], title: string, onClose?: () => void): void {
    this.show(new ChestScreen(this.deps, this.stage, contents, title), onClose);
  }

  private show(screen: ContainerScreen, onClose?: () => void): void {
    if (this.current) this.close();
    this.current = screen;
    this.closeCb = onClose ?? null;
    try { document.exitPointerLock?.(); } catch { /* not locked */ }
    this.stage.setGuiScaleSetting(this.deps.guiScale());
    this.lastScale = this.deps.guiScale();
    this.stage.show();
    screen.layout(this.stage.width, this.stage.height);
    screen.mountInto(this.stage.content);
    screen.pointerMove(this.px, this.py);
    screen.refresh(true);
  }

  close(): void {
    const c = this.current;
    if (!c) return;
    this.current = null;
    c.close();
    this.stage.hide();
    Tooltip.hide();
    const cb = this.closeCb;
    this.closeCb = null;
    cb?.();
    this.onClosed?.();
  }

  private relayout(): void {
    if (this.current) {
      this.current.layout(this.stage.width, this.stage.height);
      this.current.refresh(false);
    }
  }

  update(dt: number): void {
    const c = this.current;
    if (!c) return;
    const gs = this.deps.guiScale();
    if (gs !== this.lastScale) {
      this.lastScale = gs;
      this.stage.setGuiScaleSetting(gs);
      this.relayout();
    }
    c.update(dt);
    c.refresh(false);
  }

  /** See the header: returns true if the screen consumed the key. */
  handleKey(e: KeyboardEvent): boolean {
    const c = this.current;
    if (!c) return false;
    if (e.code === 'Escape') { this.close(); return true; }
    if (c.keyDown(e)) return true;
    if (e.code === 'KeyE') { this.close(); return true; }
    const m = /^Digit([1-9])$/.exec(e.code);
    if (m) { c.hotbarSwap(+m[1] - 1); return true; }
    if (e.code === 'KeyQ') { c.dropHovered(e.ctrlKey); return true; }
    return true;
  }
}
